import { Alert, Backdrop, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import Page from "../../minimals/components/page";
import { UploadMultiFile } from "../components/upload";
import { useCallback, useState } from "react";
import axios from "axios";
import { stripDataUriPrefix } from "../utils/stripDataUriPrefix";
import { toBase64 } from "../utils/helpers";
import LinearWithValueLabel from "./LinearProgressWithValueLabel";
import ExtractedFieldsDisplay from "./ExtractedFieldsDisplay";
import PdfViewer from "./PdfViewerWithLocation";
import { convertToHighlights } from "./convertToHighlights";
import { FieldData } from "./types";

export default function CustomDocumentExtractor() {
  const [file, setFile] = useState<(File & { preview?: string }) | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [extractedFieldsWithHighlights, setExtractedFieldsWithHighlights] = useState<Record<string, FieldData> | null>(
    null
  );
  const [customPrompt, setCustomPrompt] = useState<string | null>(null);
  const [base64String, setBase64String] = useState<string | null>(null);

  const submit = async () => {
    setExtractedFieldsWithHighlights(null);
    setSubmitting(true);
    const base64String = stripDataUriPrefix(await toBase64(file as File));
    setBase64String(base64String);
    const { data } = await axios.post("/api/documentExtractor/customDocumentExtraction", {
      base64String,
      customPrompt
    });
    setExtractedFieldsWithHighlights(data);
    setSubmitting(false);
  };

  const handleFileDrop = useCallback(
    (acceptedFile: any) => {
      setExtractedFieldsWithHighlights(null);
      const file = Object.assign(acceptedFile[0], {
        preview: URL.createObjectURL(acceptedFile[0])
      }) as File & { preview: string };
      const extension = acceptedFile[0].name?.substring(acceptedFile[0].name?.lastIndexOf("."));
      setFile(file);
    },
    [setFile]
  );

  const hasIssue = extractedFieldsWithHighlights?.hasOwnProperty("issue");

  return (
    <Page title="Custom Document Extractor">
      <Stack direction="row" justifyContent="center" spacing={3}>
        <Stack direction="column" width={800} spacing={2}>
          <Typography>Upload Document To Extract Fields</Typography>
          <UploadMultiFile
            files={file ? [file] : []}
            onDrop={handleFileDrop}
            onRemove={() => {
              setFile(null);
            }}
            onRemoveAll={() => {
              setFile(null);
            }}
            showPreview={false}
            showSelectedFiles={true}
            multiple={false}
          />
          <TextField
            rows={12}
            onChange={(e) => setCustomPrompt(e.target.value)}
            label="Type the prompt you would like the AI model to use"
            placeholder="Example 1: Extract the purchase price and the balance after the initial deposit. Example 2: Purchase Price, Balance After Initial Deposit. Example 3: All fields from this document."
            multiline
          />
          <Button variant="contained" onClick={submit} size="large">
            Submit For Extraction
          </Button>
          {submitting && (
            <LinearWithValueLabel loading={submitting} completed={!submitting && extractedFieldsWithHighlights} />
          )}
          {!hasIssue && <ExtractedFieldsDisplay data={extractedFieldsWithHighlights} />}
          {hasIssue && (
            <Alert variant="filled" severity="warning">
              {(extractedFieldsWithHighlights as any).issue.value}
            </Alert>
          )}
        </Stack>
        <Box
          mt={2}
          sx={{
            width: 800,
            position: "sticky",
            top: 24,
            alignSelf: "flex-start",
            height: "calc(100vh - 48px)"
          }}
        >
          {!extractedFieldsWithHighlights && file && file.type === "application/pdf" && (
            <iframe
              title="PDF Preview"
              src={`${file?.preview}#zoom=100`}
              style={{ width: "100%", minHeight: "100vh", border: "none", padding: 10 }}
            />
          )}
          {base64String && extractedFieldsWithHighlights && (
            <PdfViewer pdfData={base64String} highlights={convertToHighlights(extractedFieldsWithHighlights)} />
          )}
        </Box>
      </Stack>
    </Page>
  );
}
